import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField, List, ListItem, IconButton, Grid, Autocomplete, Select, MenuItem, FormControl
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { getLocations } from '../../../services/LocationService';
import { getReferences } from '../../../services/SuppliesService';
import { getCategoriesByType } from '../../../services/CategoryService';
import { createStockMovement } from '../../../services/StockService';
import dayjs from 'dayjs';
import { referenceTypes, getReferenceTypeName, getUnitById, categoryOptions } from '../../../utils/Enums';

const AddStockMovementModal = ({ open, onClose, onSave }) => {

  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [locations, setLocations] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [destinationStockItems, setDestinationStockItems] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [rawDestinationData, setRawDestinationData] = useState([]);

  const [formData, setFormData] = useState({
    date: dayjs(),
    sourceBranch: '',
    destinationBranch: '',
    items: [],
  });

  useEffect(() => {
    if (open) {
      getLocations().then(setLocations).catch(console.error);
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (formData.sourceBranch) {
      getReferences(formData.sourceBranch)
        .then((data) => {
          const filteredData = data.filter(item => item.referenceType !== referenceTypes.CATEGORIES);
          const formattedItems = filteredData.map(item => ({
            referenceType: item.referenceType,
            referenceId: item.referenceId2 || item.referenceId1,
            referenceId1: item.referenceId1,
            referenceId2: item.referenceId2,
            categoryId: item.categoryId || null,
            name: `${item.referenceName2 
              ? `${item.referenceName1} - ${item.referenceName2}` 
              : item.referenceName1} - ${getReferenceTypeName(item.referenceType)}`,
            stock: item.stock,
            unit: item.unit,
            stockControlMode: item.stockControlMode || item.stock_control_mode ||
              (item.tubIceCream ? 'FRACTIONAL' : 'UNITARY')
          }));

          setStockItems(formattedItems);

          setFormData(prev => ({
            ...prev,
            items: prev.items.map(item => {
              const updatedProduct = formattedItems.find(p => 
                p.referenceId === item.product?.referenceId && 
                p.referenceType === item.product?.referenceType
              );
              return {
                ...item,
                stock: updatedProduct ? updatedProduct.stock : 0
              };
            })
          }));
        })
        .catch(console.error);
    }
  }, [formData.sourceBranch]); 

  // Cargar categorías cuando se selecciona un tipo
  useEffect(() => {
    console.log('[AddStockMovementModal] Filter type changed:', filterType);
    if (filterType && formData.destinationBranch) {
      let categoryTypeId;
      const filterTypeNum = parseInt(filterType);
      console.log('[AddStockMovementModal] Filter type number:', filterTypeNum);
      console.log('[AddStockMovementModal] Reference types:', referenceTypes);
      
      if (filterTypeNum === referenceTypes.PRODUCTS) {
        categoryTypeId = categoryOptions.PRODUCTS;
      } else if (filterTypeNum === referenceTypes.PREPARADO) {
        categoryTypeId = categoryOptions.PREPARATIONS;
      } else if (filterTypeNum === referenceTypes.INSUMO) {
        categoryTypeId = categoryOptions.SUPPLIES;
      }
      
      console.log('[AddStockMovementModal] Category type ID:', categoryTypeId);
      
      if (categoryTypeId) {
        getCategoriesByType(categoryTypeId)
          .then(cats => {
            console.log('[AddStockMovementModal] Categories loaded:', cats);
            setCategories(cats);
          })
          .catch(console.error);
      } else {
        console.log('[AddStockMovementModal] No category type ID, clearing categories');
        setCategories([]);
      }
    } else {
      console.log('[AddStockMovementModal] No filter type or destination, clearing categories');
      setCategories([]);
    }
    setFilterCategory(''); // Resetear categoría cuando cambia el tipo
  }, [filterType, formData.destinationBranch]);

  useEffect(() => {
    if (formData.destinationBranch) {
      getReferences(formData.destinationBranch)
        .then((data) => {
          console.log('[AddStockMovementModal] Raw destination data:', data);
          setRawDestinationData(data);
          const filteredData = data.filter(item => item.referenceType !== referenceTypes.CATEGORIES);
          console.log('[AddStockMovementModal] Filtered data (sin CATEGORIES):', filteredData);
          
          const formattedItems = filteredData.map(item => {
            const recommendedStock = item.suggestedStock - item.stock;
            const formattedItem = {
              referenceType: item.referenceType,
              referenceId: item.referenceId2 || item.referenceId1,
              referenceId1: item.referenceId1,
              referenceId2: item.referenceId2,
              categoryId: item.categoryId || null,
              name: `${item.referenceName2 
                ? `${item.referenceName1} - ${item.referenceName2}` 
                : item.referenceName1} - ${getReferenceTypeName(item.referenceType)}`,
              stock: item.stock,
              unit: item.unit,
              suggestedStock: item.suggestedStock,
              recommendedStock: recommendedStock < 0 ? 0 : recommendedStock,
            };
            console.log('[AddStockMovementModal] Formatted item:', formattedItem);
            console.log('[AddStockMovementModal] Original item categoryId:', item.categoryId);
            return formattedItem;
          });
          
          console.log('[AddStockMovementModal] All formatted items:', formattedItems);
          setDestinationStockItems(formattedItems);
        })
        .catch(console.error);
    }
  }, [formData.destinationBranch]);

  // Función para cargar items según los filtros seleccionados
  const handleLoadItems = async () => {
    console.log('[AddStockMovementModal] ===== handleLoadItems START =====');
    console.log('[AddStockMovementModal] Destination branch:', formData.destinationBranch);
    console.log('[AddStockMovementModal] Filter type:', filterType);
    console.log('[AddStockMovementModal] Filter category:', filterCategory);
    console.log('[AddStockMovementModal] Destination stock items count:', destinationStockItems.length);
    console.log('[AddStockMovementModal] All destination stock items:', destinationStockItems);
    
    if (!formData.destinationBranch || destinationStockItems.length === 0) {
      console.log('[AddStockMovementModal] Early return: no destination branch or no items');
      return;
    }

    setLoadingItems(true);
    try {
      // Simular un pequeño delay para mejor UX (opcional)
      await new Promise(resolve => setTimeout(resolve, 300));

      // Aplicar filtros (si están seleccionados)
      let filteredItems = destinationStockItems;
      console.log('[AddStockMovementModal] Items before filters:', filteredItems.length, filteredItems);
      
      if (filterType) {
        const filterTypeNum = parseInt(filterType);
        console.log('[AddStockMovementModal] Filtering by type:', filterTypeNum);
        filteredItems = filteredItems.filter(item => {
          const matches = item.referenceType === filterTypeNum;
          console.log(`[AddStockMovementModal] Item ${item.name} - referenceType: ${item.referenceType}, matches: ${matches}`);
          return matches;
        });
        console.log('[AddStockMovementModal] Items after type filter:', filteredItems.length, filteredItems);
      }
      
      if (filterCategory) {
        const filterCategoryNum = parseInt(filterCategory);
        console.log('[AddStockMovementModal] Filtering by category:', filterCategoryNum);
        console.log('[AddStockMovementModal] Items before category filter:', filteredItems.length);
        filteredItems = filteredItems.filter(item => {
          const matches = item.categoryId === filterCategoryNum;
          console.log(`[AddStockMovementModal] Item ${item.name} - categoryId: ${item.categoryId}, filterCategory: ${filterCategoryNum}, matches: ${matches}`);
          return matches;
        });
        console.log('[AddStockMovementModal] Items after category filter:', filteredItems.length, filteredItems);
      }
      
      // Filtrar productos donde stock < suggestedStock
      console.log('[AddStockMovementModal] Filtering items where stock < suggestedStock');
      const itemsToAdd = filteredItems
        .filter(item => {
          const needsStock = item.stock < item.suggestedStock;
          console.log(`[AddStockMovementModal] Item ${item.name} - stock: ${item.stock}, suggestedStock: ${item.suggestedStock}, needsStock: ${needsStock}`);
          return needsStock;
        })
        .map(item => {
          const matchingOrigin = stockItems.find(
            originItem =>
              originItem.referenceId === item.referenceId &&
              originItem.referenceType === item.referenceType
          );

          return {
            id: Date.now() + Math.random(),
            product: item,
            stock: matchingOrigin?.stock ?? 0,
            destinationStock: item.stock,
            unit: matchingOrigin?.stockControlMode === 'FRACTIONAL' ? 1 : item.unit,
            recommendedStock: item.recommendedStock,
            quantity: '',
            error: false
          };
        });
      
      console.log('[AddStockMovementModal] Items to add:', itemsToAdd.length, itemsToAdd);

      // Agregar solo los items que no están ya en el listado
      setFormData(prev => {
        const existingIds = new Set(prev.items.map(item => 
          `${item.product?.referenceId}-${item.product?.referenceType}`
        ));
        console.log('[AddStockMovementModal] Existing item IDs:', Array.from(existingIds));
        
        const newItems = itemsToAdd.filter(item => {
          const itemKey = `${item.product.referenceId}-${item.product.referenceType}`;
          const isNew = !existingIds.has(itemKey);
          console.log(`[AddStockMovementModal] Item ${item.product.name} - key: ${itemKey}, isNew: ${isNew}`);
          return isNew;
        });

        console.log('[AddStockMovementModal] New items to add:', newItems.length, newItems);
        console.log('[AddStockMovementModal] Total items after adding:', prev.items.length + newItems.length);
        console.log('[AddStockMovementModal] ===== handleLoadItems END =====');

        return {
          ...prev,
          items: [...prev.items, ...newItems],
        };
      });
    } catch (error) {
      console.error('[AddStockMovementModal] Error loading items:', error);
    } finally {
      setLoadingItems(false);
    }
  };  

  const resetForm = () => {
    setFormData({
      date: dayjs(),
      sourceBranch: '',
      destinationBranch: '',
      items: [],
    });
    setStockItems([]);
    setDestinationStockItems([]);
    setFilterType('');
    setFilterCategory('');
    setCategories([]);
    setRawDestinationData([]);
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === "sourceBranch" ? { destinationBranch: "" } : {})
    }));
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { id: Date.now(), product: null, quantity: '', stock: 0, error: false }]
    }));
  };

  const handleItemChange = (index, field, value) => {
  setFormData(prev => {
    const updatedItems = prev.items.map((item, idx) => {
      if (idx === index) {
        if (field === "product") {
          
          const selectedProduct = stockItems.find(p =>
            p.referenceId === value?.referenceId && p.referenceType === value?.referenceType
          );
          
          const destinationProduct = destinationStockItems.find(p =>
            p.referenceId === value?.referenceId && p.referenceType === value?.referenceType
          );

          const stockOrigen = selectedProduct?.stock ?? 0;
          const stockDestino = destinationProduct?.stock ?? 0;
          const sugerido = destinationProduct?.suggestedStock ?? 0;
          const recommendedStock = Math.max(sugerido - stockDestino, 0);

          return {
            ...item,
            product: value,
            stock: stockOrigen,
            destinationStock: stockDestino,
            unit: value?.stockControlMode === 'FRACTIONAL' ? 1 : selectedProduct?.unit ?? null,
            recommendedStock,
            quantity: '',
            error: false,
          };

        }

        if (field === "quantity") {
          const newQuantity = parseFloat(value);
          return {
            ...item,
            quantity: value,
            error: isNaN(newQuantity) || newQuantity <= 0,
          };
        }
      }
      return item;
    });

    return { ...prev, items: updatedItems };
  });
};

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== index)
    }));
  };

  const isValidForm = () => {
    if (!formData.date || !formData.sourceBranch || !formData.destinationBranch) return false;
    if (formData.items.length === 0) return false;
    return formData.items.every(item => item.product && item.quantity && !item.error);
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const movementData = {
        movement_date: formData.date && dayjs(formData.date).isValid()
          ? dayjs(formData.date).format('YYYY-MM-DDTHH:mm:ss') 
          : null,
        source_branch: parseInt(formData.sourceBranch),
        destination_branch: parseInt(formData.destinationBranch),
        items: formData.items.map(item => ({
          reference_type: item.product.referenceType,
          reference_id: item.product.referenceId,
          quantity: parseFloat(item.quantity),
          unit: parseFloat(item.unit),
        })),
      };
      
      const savedData = await createStockMovement(movementData);
      onSave(savedData);
      onClose();
    } catch (error) {
      console.error('Error saving stock movement:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => {}} maxWidth="lg" fullWidth>
      <DialogTitle>{t('add_stock_movement')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={4} style={{marginTop: "8px"}}>
            <DatePicker
              label={t('date')}
              value={formData.date ? dayjs(formData.date, 'YYYY-MM-DDTHH:mm:ss') : null}
              onChange={(date) => setFormData({ 
                ...formData, 
                date: date ? date.format('YYYY-MM-DDTHH:mm:ss') : '' 
              })}
              renderInput={(params) => <TextField {...params} fullWidth />}
              format="DD/MM/YYYY"
            />
          </Grid>
          <Grid item xs={12} sm={4} style={{marginTop: "8px"}}>
            <FormControl fullWidth>
              <Select
                name="sourceBranch"
                value={formData.sourceBranch}
                onChange={handleInputChange}
                displayEmpty
              >
                <MenuItem value="" disabled>{t('select_source_branch')}</MenuItem>
                {locations.map(location => (
                  <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} style={{marginTop: "8px"}}>
            <FormControl fullWidth>
              <Select
                name="destinationBranch"
                value={formData.destinationBranch}
                onChange={handleInputChange}
                displayEmpty
              >
                <MenuItem value="" disabled>{t('select_destination_branch')}</MenuItem>
                {locations.map(location => (
                  <MenuItem 
                    key={location.id} 
                    value={location.id} 
                    disabled={location.id === formData.sourceBranch}
                  >
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Filtros */}
        {formData.destinationBranch && (
          <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">{t('all_types') || 'Todos los tipos'}</MenuItem>
                  <MenuItem value={referenceTypes.PRODUCTS}>{t('product') || 'Producto'}</MenuItem>
                  <MenuItem value={referenceTypes.PREPARADO}>{t('preparation') || 'Preparación'}</MenuItem>
                  <MenuItem value={referenceTypes.INSUMO}>{t('supply') || 'Insumo'}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={!filterType || categories.length === 0}>
                <Select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">{t('all_categories') || 'Todas las categorías'}</MenuItem>
                  {categories.map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <LoadingButton
                variant="contained"
                fullWidth
                onClick={handleLoadItems}
                disabled={!formData.destinationBranch}
                loading={loadingItems}
                startIcon={!loadingItems ? getIcon('add') : null}
              >
                {loadingItems ? (t('loading') || 'Cargando...') : (t('load') || 'Cargar')}
              </LoadingButton>
            </Grid>
          </Grid>
        )}

        <List style={{marginTop: "12px"}}>
          {formData.items.map((item, index) => (
            <ListItem key={item.id} sx={{ display: 'flex', gap: 2, alignItems: 'inherit' }}>
              <Autocomplete
                options={stockItems}
                getOptionLabel={(option) => option.name || ""}
                value={item.product || null}
                onChange={(event, newValue) => handleItemChange(index, "product", newValue)}
                renderInput={(params) => <TextField {...params} label={t("product")} fullWidth />}
                sx={{ flex: 2 }}
              />

              {/* Stock origen actual */}
              <TextField 
                label={t("source_stock")}
                value={`${(item.stock ?? 0).toFixed(1)} ${getUnitById(item.unit)?.label ?? ''}`} 
                disabled 
                sx={{ flex: 1 }} 
              />

              {/* Stock destino actual */}
              <TextField 
                label={t("destination_stock")}
                value={`${(item.destinationStock ?? 0).toFixed(1)} ${getUnitById(item.unit)?.label ?? ''}`} 
                disabled 
                sx={{ flex: 1 }} 
              />

              {/* Cantidad sugerida */}
              <TextField 
                label={
                  item.recommendedStock !== undefined && item.recommendedStock !== null
                    ? `${t('suggested_quantity')}: ${item.recommendedStock.toFixed(1)}` 
                    : t('quantity')
                }
                type="number" 
                value={item.quantity} 
                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} 
                sx={{ flex: 1 }} 
                error={item.error} 
                helperText={item.error ? t('invalid_quantity') : ""}
                InputLabelProps={{ shrink: true }}
              />

              <IconButton onClick={() => handleRemoveItem(index)}>{getIcon('close')}</IconButton>
            </ListItem>
          ))}
        </List>

        <Box textAlign="center" mt={2}>
          <Button variant="outlined" color="error" startIcon={getIcon('trash')} onClick={() => setFormData(prev => ({ ...prev, items: [] }))}>
            {t('clear_all')}
          </Button>
          <Button variant="outlined" startIcon={getIcon('add')} onClick={handleAddItem} style={{marginLeft: "16px"}}>
            {t('add_item')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <LoadingButton loading={loading} disabled={!isValidForm()} onClick={handleSave} variant="contained">{t('save')}</LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddStockMovementModal;
